import { forwardRef, useCallback, useEffect, useRef } from 'react'
import { SGeneralForms } from 'shj-components'
import { TabComponentInterface } from '../../../index.d'
import { useCustomerInfo } from '@/hooks/useCustomerInfo'
import { Skeleton } from 'antd'
import dayjs from 'dayjs'
import CheckThePhone from '@/components/CheckThePhone'
import UrlConfig from '@/api/UrlConfigs'

const BaseInfo = forwardRef<TabComponentInterface>(() => {
  const { customerInfo, loading, customerSourceList } = useCustomerInfo()
  const formRef = useRef<any>()

  // 获取级联选择器值对应的文本表示
  const getChannelText = useCallback(
    (channelIds?: string[]) => {
      if (
        !channelIds ||
        channelIds.length === 0 ||
        !customerSourceList ||
        customerSourceList.length === 0
      ) {
        return '-'
      }

      // 递归查找级联值对应的文本
      const findOptionTexts = (
        options: any[],
        ids: string[],
        currentIndex: number = 0,
        result: string[] = []
      ) => {
        if (currentIndex >= ids.length) {
          return result
        }

        const currentId = ids[currentIndex]
        const currentOption = options.find(opt => opt.value === currentId)

        if (!currentOption) {
          return result
        }

        result.push(currentOption.label)

        // 如果有子选项且还有更多ID需要匹配，继续递归
        if (currentOption.children && currentIndex + 1 < ids.length) {
          return findOptionTexts(currentOption.children, ids, currentIndex + 1, result)
        }

        return result
      }

      const textArray = findOptionTexts(customerSourceList, channelIds)
      return textArray.length > 0 ? textArray.join(' / ') : '-'
    },
    [customerSourceList]
  )

  useEffect(() => {
    // 设置表单值
    if (customerInfo?.id) {
      formRef?.current?.onFormRef().setFieldsValue({
        ...customerInfo,
        birthday: customerInfo?.birthday ? dayjs(customerInfo?.birthday).format('YYYY-MM-DD') : '',
        createdTime: customerInfo?.createdTime
          ? dayjs(customerInfo?.createdTime).format('YYYY-MM-DD HH:mm:ss')
          : '',
        lastUpdatedTime: customerInfo?.lastUpdatedTime
          ? dayjs(customerInfo?.lastUpdatedTime).format('YYYY-MM-DD HH:mm:ss')
          : '',
        orgNames: customerInfo?.orgNames ? customerInfo.orgNames?.join('；') : '',
        channelId: getChannelText(customerInfo?.channelId),
        wechatNo: customerInfo?.wechatNo ? customerInfo.wechatNo : '',
        gender: customerInfo?.gender ? customerInfo.gender : '',
        age: customerInfo?.age ? customerInfo.age : '',
      })
    }
  }, [customerInfo, customerInfo?.id, getChannelText])

  return (
    <Skeleton active loading={loading} paragraph={{ rows: 5 }}>
      <SGeneralForms
        ref={formRef}
        layout={'horizontal'}
        isDetail={true}
        configs={[
          {
            title: '客户信息',
            formItem: [
              { label: '客户编号', name: 'id', spanCol: 8 },
              { label: '客户名称', name: 'name', spanCol: 8 },
              {
                label: '客户来源',
                name: 'channelId',
                spanCol: 8,
              },
              { label: '客户性别', name: 'gender', spanCol: 8 },
              { label: '客户年龄', name: 'age', spanCol: 8 },
              { label: '客户生日', name: 'birthday', spanCol: 8 },
              {
                label: '手机号码',
                name: 'phone',
                spanCol: 8,
                children: (
                  <CheckThePhone
                    url={
                      UrlConfig.crmManageWeb.viewPhone +
                      '/' +
                      customerInfo?.id +
                      '/customerViewPhone'
                    }
                    id={customerInfo?.id}
                    initValue={customerInfo?.phone}
                    auth={'customerViewPhone'}
                  />
                ),
              },
              {
                label: '微信号码',
                name: 'wechatNo',
                spanCol: 8,
                children: (
                  <CheckThePhone
                    url={
                      UrlConfig.crmManageWeb.viewWeChat +
                      '/' +
                      customerInfo?.id +
                      '/customerViewWeChat'
                    }
                    id={customerInfo?.id + 'wechatNo'}
                    initValue={customerInfo?.wechatNo}
                    auth={'customerViewWeChat'}
                  />
                ),
              },
            ],
          },
          {
            title: '负责人信息',
            formItem: [{ label: '所属门店', name: 'orgNames', spanCol: 8 }],
          },
          {
            title: '联系人信息',
            formItem: [
              { label: '联系人姓名', name: 'contactName', spanCol: 8 },
              {
                label: '联系人手机号码',
                name: 'contactPhone',
                spanCol: 8,
                children: (
                  <CheckThePhone
                    url={`${UrlConfig.crmManageWeb.getContactPhonesByld}/${customerInfo?.contactId}`}
                    id={customerInfo?.id}
                    initValue={customerInfo?.contactPhone}
                    showValue={'phone'}
                    auth={'contactViewPhone'}
                  />
                ),
              },
              { label: '联系人关系', name: 'contactRelationName', spanCol: 8 },
            ],
          },
          {
            title: '系统信息',
            formItem: [
              { label: '创建人', name: 'createdByName', spanCol: 8 },
              { label: '创建时间', name: 'createdTime', spanCol: 8 },
              { label: '更新时间', name: 'lastUpdatedTime', spanCol: 8 },
            ],
          },
        ]}
      />
    </Skeleton>
  )
})

export default BaseInfo
